// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type Holding } from '@shared/data-access/interfaces/api/ai/holding';
import { portfolioApi } from '@shared/data-access/api/ai/portfolio-api';
import { selectCurrentPortfolioWithoutCash } from '@shared/data-access/slices/portfolio-positions';
import {
  selectWorkflowAsset,
  selectWorkflowType,
} from '@advisor-app/store/slices/workflow';
import { useAppSelector } from '@advisor-app/store/store';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface Props {
  benchmark: string;
}

export const useValidateTickersPortfolio = ({ benchmark }: Props) => {
  const assetType = useAppSelector(selectWorkflowAsset);

  const workflowType = useAppSelector(selectWorkflowType);

  const [validatedTickers, setValidatedTickers] = useState<string[]>([]);

  const [invalidValidatedTickers, setInvalidValidatedTickers] = useState<string[]>(
    [],
  );

  const [isLoadingValidation, setIsLoadingValidation] = useState(false);

  const [getValidation] = portfolioApi.useLazyPortfolioValidationQuery();

  const portfolioWithoutCash = useAppSelector(selectCurrentPortfolioWithoutCash);

  const isNotRebalanceFlow = useMemo(
    () => workflowType !== 'rebalance',
    [workflowType],
  );

  const holdings: Holding[] = useMemo(
    () =>
      portfolioWithoutCash.map(asset => ({
        ticker: asset.ticker,
        price: asset.outstanding_balance,
        qty: 1,
        unrealized_capital_gains: asset.unrealized_capital_gains,
      })),
    [portfolioWithoutCash],
  );

  const hasValues = useMemo(
    () => !holdings.some(holding => holding.price === 0),
    [holdings],
  );

  const portfolioStrategy = useMemo(
    () => ({
      holdings: holdings,
      benchmark: { target_asset: [{ ticker: benchmark, weight: 1 }] },
    }),
    [holdings, benchmark],
  );

  const holdingsTickers = useMemo(
    () => holdings.map(holding => holding.ticker),
    [holdings],
  );

  const invalidTickers = useMemo(
    () =>
      invalidValidatedTickers.filter(ticker => holdingsTickers.includes(ticker)),
    [invalidValidatedTickers, holdingsTickers],
  );

  const validateTickers = useCallback(async () => {
    setIsLoadingValidation(true);

    try {
      const { data: validatedTickers } = await getValidation({
        payload: portfolioStrategy,
        assetType,
      });

      /* istanbul ignore next */
      setValidatedTickers(prevValidatedTickers => {
        const newValidatedTickers = holdingsTickers.filter(
          ticker =>
            !prevValidatedTickers.includes(ticker) &&
            !validatedTickers?.unsupported_ticker_positions?.includes(ticker),
        );

        return [...prevValidatedTickers, ...newValidatedTickers];
      });

      /* istanbul ignore next */
      setInvalidValidatedTickers(prev => {
        const newInvalidTickers =
          validatedTickers?.unsupported_ticker_positions as string[];

        const combinedTickers = [...prev, ...newInvalidTickers];

        return [...new Set(combinedTickers)];
      });
    } catch (error) {
      console.error('[validateTickers]', error);
    }

    setIsLoadingValidation(false);
  }, [assetType, getValidation, portfolioStrategy, holdingsTickers]);

  const isSomeInvalidTicker = useMemo(
    () => (isNotRebalanceFlow ? invalidTickers?.length > 0 : false),
    [isNotRebalanceFlow, invalidTickers?.length],
  );

  const hasHoldings = useMemo(
    () => portfolioStrategy.holdings.length > 0,
    [portfolioStrategy],
  );

  const areTickersValid = useMemo(
    () => holdingsTickers.every(ticker => ticker === ticker.toUpperCase()),
    [holdingsTickers],
  );

  useEffect(() => {
    if (isNotRebalanceFlow && hasHoldings && areTickersValid) {
      const validatedSet = new Set(validatedTickers);

      const invalidSet = new Set(invalidTickers);

      const newTickers = holdingsTickers.filter(
        ticker => !validatedSet.has(ticker) && !invalidSet.has(ticker),
      );

      /* istanbul ignore next */
      if (newTickers.length > 0) {
        validateTickers();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdingsTickers]);

  useEffect(() => {
    setInvalidValidatedTickers([]);

    setValidatedTickers([]);

    if (isNotRebalanceFlow && hasHoldings) {
      validateTickers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetType]);

  return { invalidTickers, isSomeInvalidTicker, isLoadingValidation, hasValues };
};
